/**
 * Individual Migration Steps for Location-based Hierarchy
 * Allows running each migration step separately
 */

import { API, graphqlOperation } from 'aws-amplify';
import { LocationHierarchyMigration } from './migrateToLocation';
import { 
  listLocations, 
  listLocationLaborTargets, 
  getLocation, 
  listAreas2,
  listEmployees,
  listLocationEmployees,
  listOrgUnitEmployees,
  listOrgUnits,
  listUsers
} from 'graphql/queries';
import { updateLocation, createLocation, createLocationLaborTargets } from 'graphql/mutations';

// Helper to remove __typename from nested objects
function removeTypename(obj) {
  if (!obj || typeof obj !== 'object') return obj;
  
  if (Array.isArray(obj)) {
    return obj.map(item => removeTypename(item));
  }
  
  const cleaned = {};
  for (const [key, value] of Object.entries(obj)) {
    if (key !== '__typename') {
      cleaned[key] = removeTypename(value);
    }
  }
  return cleaned;
}

/**
 * Create Location records only
 */
export async function migrateLocationsOnly(clientId = 'grm') {
  const migration = new LocationHierarchyMigration(clientId);

  console.log('📊 Starting Location migration...');

  try {
    // Load existing data
    await migration.fetchExistingLocations();
    await migration.fetchExistingOrgUnits();

    // IMPORTANT: Fetch StoreRecords for ID mapping
    await migration.fetchExistingStoreRecords();

    // Fetch legacy data
    const legacyData = await migration.fetchLegacyData();

    // Calculate counts
    const totalLegacyStores = legacyData.reduce((sum, area) => sum + area.stores.length, 0);
    const existingCount = migration.existingLocations.size;
    const toCreate = totalLegacyStores - existingCount;

    console.log(`📊 Location Summary:`);
    console.log(`   Total legacy stores: ${totalLegacyStores}`);
    console.log(`   Existing Locations: ${existingCount}`);
    console.log(`   To create: ${toCreate}`);
    console.log(`   StoreRecords available for mapping: ${migration.existingStoreRecords.size}`);

    // Ensure OrgUnits exist
    await migration.ensureOrgUnits(legacyData);

    // Migrate only locations
    await migration.migrateLocations(legacyData);
    
    // Check for any errors
    if (migration.results.locations.errors.length > 0) {
      console.error('❌ Location migration failed with errors:');
      migration.results.locations.errors.forEach(err => {
        console.error(`   - ${err.store}: ${err.error}`);
      });
      
      return {
        success: false,
        locations: migration.results.locations,
        orgUnits: migration.results.orgUnits,
        counts: {
          total: totalLegacyStores,
          existing: existingCount,
          created: migration.results.locations.created,
          failed: migration.results.locations.errors.length
        },
        error: 'Some locations failed to migrate. Check console for details.'
      };
    }
    
    // Print summary
    console.log('\n========================================');
    console.log('📊 LOCATION MIGRATION COMPLETE');
    console.log('========================================');
    console.log(`✅ Total Locations created: ${migration.results.locations.created}`);
    console.log(`   - With StoreRecord.id: ${migration.results.locations.withStoreRecordId} (MetricData compatible)`);
    console.log(`   - With generated UUID: ${migration.results.locations.withGeneratedId} (No MetricData linkage)`);

    // List unmatched stores if any
    if (migration.unmatchedStores && migration.unmatchedStores.length > 0) {
      console.log('\n⚠️ Stores without StoreRecord match:');
      migration.unmatchedStores.forEach((store, index) => {
        console.log(`   ${index + 1}. ${store.shortName} (${store.longName})`);
      });
      console.log(`\n⚠️ Action: Create StoreRecords for these ${migration.unmatchedStores.length} stores to enable MetricData`);
    }

    console.log('========================================\n');

    return {
      success: true,
      locations: migration.results.locations,
      orgUnits: migration.results.orgUnits,
      unmatchedStores: migration.unmatchedStores || [],
      counts: {
        total: totalLegacyStores,
        existing: existingCount,
        created: migration.results.locations.created,
        updated: migration.results.locations.updated,
        withStoreRecordId: migration.results.locations.withStoreRecordId,
        withGeneratedId: migration.results.locations.withGeneratedId
      }
    };
    
  } catch (error) {
    console.error('❌ Fatal error during Location migration:', error);
    
    // Get current counts even on failure
    const legacyData = await migration.fetchLegacyData().catch(() => []);
    const totalLegacyStores = legacyData.reduce((sum, area) => sum + area.stores.length, 0);
    const existingCount = migration.existingLocations?.size || 0;
    
    return {
      success: false,
      error: error.message,
      locations: migration.results?.locations || { created: 0, updated: 0, withStoreRecordId: 0, withGeneratedId: 0, errors: [] },
      orgUnits: migration.results?.orgUnits || { created: 0, updated: 0, errors: [] },
      counts: {
        total: totalLegacyStores,
        existing: existingCount,
        created: migration.results?.locations?.created || 0,
        failed: migration.results?.locations?.errors?.length || 0,
        withStoreRecordId: migration.results?.locations?.withStoreRecordId || 0,
        withGeneratedId: migration.results?.locations?.withGeneratedId || 0
      }
    };
  }
}

/**
 * Create LocationLaborTargets only
 */
export async function migrateLaborTargetsOnly(clientId = 'grm') {
  const migration = new LocationHierarchyMigration(clientId);
  
  console.log('📊 Starting Labor Targets migration...');
  
  try {
    // Load existing data
    await migration.fetchExistingLocations();
    await migration.fetchExistingLaborTargets();
    
    // Fetch legacy data
    const legacyData = await migration.fetchLegacyData();
    
    // Calculate counts
    const totalLocations = migration.existingLocations.size;
    const existingTargets = migration.existingLaborTargets.size;
    const toCreate = totalLocations - existingTargets;
    
    console.log(`📊 Labor Targets Summary:`);
    console.log(`   Total Locations: ${totalLocations}`);
    console.log(`   Existing Labor Targets: ${existingTargets}`);
    console.log(`   To create: ${toCreate}`);
    
    if (totalLocations === 0) {
      console.warn('⚠️ No Locations found. Please migrate Locations first.');
      return {
        success: false,
        error: 'No Locations found. Please migrate Locations first.',
        laborTargets: migration.results.laborTargets,
        counts: {
          totalLocations: 0,
          existing: 0,
          toCreate: 0
        }
      };
    }
    
    // Migrate only labor targets
    await migration.migrateLaborTargets(legacyData);
    
    // Check for any errors
    if (migration.results.laborTargets.errors.length > 0) {
      console.error('❌ Labor Targets migration failed with errors:');
      migration.results.laborTargets.errors.forEach(err => {
        console.error(`   - ${err.store}: ${err.error}`);
      });
      
      return {
        success: false,
        laborTargets: migration.results.laborTargets,
        counts: {
          totalLocations,
          existing: existingTargets,
          created: migration.results.laborTargets.created,
          failed: migration.results.laborTargets.errors.length
        },
        error: 'Some labor targets failed to migrate. Check console for details.'
      };
    }
    
    return {
      success: true,
      laborTargets: migration.results.laborTargets,
      counts: {
        totalLocations,
        existing: existingTargets,
        created: migration.results.laborTargets.created,
        updated: migration.results.laborTargets.updated
      }
    };
    
  } catch (error) {
    console.error('❌ Fatal error during Labor Targets migration:', error);
    
    // Get current counts even on failure
    const totalLocations = migration.existingLocations?.size || 0;
    const existingTargets = migration.existingLaborTargets?.size || 0;
    
    return {
      success: false,
      error: error.message,
      laborTargets: migration.results?.laborTargets || { created: 0, updated: 0, errors: [] },
      counts: {
        totalLocations,
        existing: existingTargets,
        created: migration.results?.laborTargets?.created || 0,
        failed: migration.results?.laborTargets?.errors?.length || 0
      }
    };
  }
}

/**
 * Create ManagerOrgAssignments only
 */
export async function migrateManagersOnly(clientId = 'grm') {
  const migration = new LocationHierarchyMigration(clientId);
  
  console.log('📊 Starting Manager Assignments migration...');
  
  try {
    // Load existing data
    await migration.fetchExistingOrgUnits();
    await migration.fetchExistingLocations();
    
    // Fetch legacy data
    const legacyData = await migration.fetchLegacyData();
    
    // Calculate potential managers count
    let totalManagers = 0;
    const uniqueManagers = new Set();
    
    legacyData.forEach(area => {
      if (area.areaManager) uniqueManagers.add(area.areaManager);
      area.stores.forEach(store => {
        if (store.gm && Array.isArray(store.gm)) {
          store.gm.forEach(mgr => uniqueManagers.add(mgr));
        }
      });
    });
    
    totalManagers = uniqueManagers.size;
    
    console.log(`📊 Manager Assignments Summary:`);
    console.log(`   Unique managers found: ${totalManagers}`);
    console.log(`   OrgUnits available: ${migration.existingOrgUnits.size}`);
    console.log(`   Locations available: ${migration.existingLocations.size}`);
    
    // Migrate only managers
    await migration.migrateManagerAssignments(legacyData);
    
    // Check for any errors
    if (migration.results.managers.errors.length > 0) {
      console.error('❌ Manager Assignments migration failed with errors:');
      migration.results.managers.errors.forEach(err => {
        console.error(`   - ${err.area || err.store}: ${err.error}`);
      });
      
      return {
        success: false,
        managers: migration.results.managers,
        counts: {
          totalManagers,
          created: migration.results.managers.created,
          failed: migration.results.managers.errors.length
        },
        error: 'Some manager assignments failed. Check console for details.'
      };
    }
    
    return {
      success: true,
      managers: migration.results.managers,
      counts: {
        totalManagers,
        created: migration.results.managers.created
      }
    };
    
  } catch (error) {
    console.error('❌ Fatal error during Manager Assignments migration:', error);
    return {
      success: false,
      error: error.message,
      managers: migration.results.managers
    };
  }
}

/**
 * Create LocationAssignmentHistory only
 * @deprecated LocationAssignmentHistory table has been removed from schema
 */
export async function migrateHistoryOnly(clientId = 'grm') {
  console.log('⚠️ LocationAssignmentHistory table has been removed from schema');
  console.log('History is now tracked in HierarchyChangeLog table');
  
  return {
    success: true,
    message: 'LocationAssignmentHistory table removed - history now in HierarchyChangeLog',
    history: {
      created: 0,
      updated: 0,
      skipped: 0,
      errors: []
    }
  };
}

/**
 * Clean Location metadata (remove __typename, preserve toast data)
 */
export async function cleanLocationMetadata(clientId = 'grm') {
  console.log('🧹 Starting Location metadata cleanup...');
  
  const results = {
    cleaned: 0,
    errors: [],
    skipped: 0
  };
  
  try {
    // Fetch all locations
    let nextToken = null;
    let allLocations = [];
    
    do {
      const result = await API.graphql(
        graphqlOperation(listLocations, {
          filter: { clientId: { eq: clientId } },
          limit: 100,
          nextToken
        })
      );
      
      const items = result.data.listLocations?.items || [];
      allLocations = allLocations.concat(items);
      nextToken = result.data.listLocations?.nextToken;
    } while (nextToken);
    
    console.log(`Found ${allLocations.length} locations to check`);
    
    // Process each location
    for (const location of allLocations) {
      try {
        if (!location.metadata) {
          results.skipped++;
          continue;
        }
        
        // Parse existing metadata
        let metadata = {};
        try {
          metadata = JSON.parse(location.metadata);
        } catch (e) {
          console.warn(`Invalid JSON in metadata for ${location.locationName}, skipping`);
          results.skipped++;
          continue;
        }
        
        // Check if cleanup is needed
        const hasTypename = JSON.stringify(metadata).includes('__typename');
        
        if (hasTypename) {
          console.log(`Cleaning metadata for ${location.locationName}`);
          
          // Clean the metadata
          const cleanedMetadata = removeTypename(metadata);
          
          // Update the location
          await API.graphql(
            graphqlOperation(updateLocation, {
              input: {
                id: location.id,
                metadata: JSON.stringify(cleanedMetadata)
              }
            })
          );
          
          results.cleaned++;
          console.log(`✅ Cleaned metadata for ${location.locationName}`);
        } else {
          results.skipped++;
        }
        
      } catch (error) {
        console.error(`Failed to clean metadata for ${location.locationName}:`, error);
        results.errors.push({
          location: location.locationName,
          error: error.message
        });
      }
    }
    
    console.log('\n📊 Metadata Cleanup Results:');
    console.log(`   Cleaned: ${results.cleaned}`);
    console.log(`   Skipped: ${results.skipped}`);
    console.log(`   Errors: ${results.errors.length}`);
    
    return results;
    
  } catch (error) {
    console.error('Failed to clean metadata:', error);
    throw error;
  }
}

/**
 * Get current migration status counts
 */
export async function getMigrationCounts(clientId = 'grm') {
  console.log('📊 Fetching migration counts...');
  
  try {
    // Fetch legacy data
    const legacyResult = await API.graphql(
      graphqlOperation(listAreas2, {
        report: 'store-admin',
        role: 'Admin',
        user: 'grmAdmin'
      })
    );
    const legacyData = legacyResult.data?.listAreas2 || [];
    const totalLegacyStores = legacyData.reduce((sum, area) => sum + area.stores.length, 0);
    
    // Fetch existing Locations
    let existingLocations = 0;
    let nextToken = null;
    do {
      const result = await API.graphql(
        graphqlOperation(listLocations, {
          filter: { clientId: { eq: clientId } },
          limit: 100,
          nextToken
        })
      );
      existingLocations += (result.data.listLocations?.items || []).length;
      nextToken = result.data.listLocations?.nextToken;
    } while (nextToken);
    
    // Fetch existing Labor Targets
    let existingLaborTargets = 0;
    nextToken = null;
    do {
      const result = await API.graphql(
        graphqlOperation(listLocationLaborTargets, {
          filter: { clientId: { eq: clientId } },
          limit: 100,
          nextToken
        })
      );
      existingLaborTargets += (result.data.listLocationLaborTargets?.items || []).length;
      nextToken = result.data.listLocationLaborTargets?.nextToken;
    } while (nextToken);
    
    // Count managers for different purposes
    const uniqueManagers = new Set();
    let totalStoreManagers = 0; // Total gm entries across all stores (for LocationEmployee)
    let totalAreaManagers = 0; // Total area managers (for OrgUnitEmployee)
    
    legacyData.forEach(area => {
      if (area.areaManager) {
        uniqueManagers.add(area.areaManager);
        totalAreaManagers++;
      }
      area.stores.forEach(store => {
        if (store.gm && Array.isArray(store.gm)) {
          store.gm.forEach(mgr => {
            uniqueManagers.add(mgr);
            totalStoreManagers++; // Count each gm assignment
          });
        }
      });
    });
    
    // Fetch existing OrgUnits
    let existingOrgUnits = 0;
    nextToken = null;
    do {
      const result = await API.graphql(
        graphqlOperation(listOrgUnits, {
          filter: { clientId: { eq: clientId } },
          limit: 100,
          nextToken
        })
      );
      existingOrgUnits += (result.data.listOrgUnits?.items || []).length;
      nextToken = result.data.listOrgUnits?.nextToken;
    } while (nextToken);
    
    // Fetch Users for Employee counts
    let totalUsers = 0;
    nextToken = null;
    do {
      const result = await API.graphql(
        graphqlOperation(listUsers, {
          filter: { client: { eq: clientId } },
          limit: 100,
          nextToken
        })
      );
      totalUsers += (result.data.listUsers?.items || []).length;
      nextToken = result.data.listUsers?.nextToken;
    } while (nextToken);
    
    // Fetch existing Employees
    let existingEmployees = 0;
    nextToken = null;
    do {
      const result = await API.graphql(
        graphqlOperation(listEmployees, {
          filter: { clientId: { eq: clientId } },
          limit: 100,
          nextToken
        })
      );
      existingEmployees += (result.data.listEmployees?.items || []).length;
      nextToken = result.data.listEmployees?.nextToken;
    } while (nextToken);
    
    // Fetch existing LocationEmployees
    let existingLocationEmployees = 0;
    nextToken = null;
    do {
      const result = await API.graphql(
        graphqlOperation(listLocationEmployees, {
          filter: { clientId: { eq: clientId } },
          limit: 100,
          nextToken
        })
      );
      existingLocationEmployees += (result.data.listLocationEmployees?.items || []).length;
      nextToken = result.data.listLocationEmployees?.nextToken;
    } while (nextToken);
    
    // Fetch existing OrgUnitEmployees
    let existingOrgUnitEmployees = 0;
    nextToken = null;
    do {
      const result = await API.graphql(
        graphqlOperation(listOrgUnitEmployees, {
          filter: { clientId: { eq: clientId } },
          limit: 100,
          nextToken
        })
      );
      existingOrgUnitEmployees += (result.data.listOrgUnitEmployees?.items || []).length;
      nextToken = result.data.listOrgUnitEmployees?.nextToken;
    } while (nextToken);
    
    return {
      success: true,
      counts: {
        locations: {
          total: totalLegacyStores,
          existing: existingLocations,
          toCreate: Math.max(0, totalLegacyStores - existingLocations)
        },
        laborTargets: {
          totalLocations: existingLocations,
          existing: existingLaborTargets,
          toCreate: Math.max(0, existingLocations - existingLaborTargets)
        },
        managers: {
          uniqueManagers: uniqueManagers.size,
          potentialAssignments: 'Multiple per manager'
        },
        history: {
          totalLocations: existingLocations,
          toCreate: existingLocations // One history record per location
        },
        orgUnits: {
          total: legacyData.length, // Total areas
          existing: existingOrgUnits,
          toCreate: Math.max(0, legacyData.length - existingOrgUnits)
        },
        employees: {
          total: totalUsers,
          existing: existingEmployees,
          toCreate: Math.max(0, totalUsers - existingEmployees)
        },
        locationEmployees: {
          total: totalStoreManagers, // Total gm assignments across all stores
          existing: existingLocationEmployees,
          toCreate: Math.max(0, totalStoreManagers - existingLocationEmployees)
        },
        orgUnitEmployees: {
          total: totalAreaManagers, // Total area manager assignments
          existing: existingOrgUnitEmployees,
          toCreate: Math.max(0, totalAreaManagers - existingOrgUnitEmployees)
        }
      }
    };
  } catch (error) {
    console.error('Failed to get migration counts:', error);
    return {
      success: false,
      error: error.message
    };
  }
}

/**
 * Merge existing metadata with new migration data
 * Preserves toast data and other existing fields
 */
export async function mergeLocationMetadata(locationId, newMetadata) {
  try {
    // Fetch existing location
    const result = await API.graphql(
      graphqlOperation(getLocation, { id: locationId })
    );
    
    const location = result.data.getLocation;
    
    if (!location) {
      throw new Error(`Location ${locationId} not found`);
    }
    
    // Parse existing metadata
    let existingMetadata = {};
    if (location.metadata) {
      try {
        existingMetadata = JSON.parse(location.metadata);
      } catch (e) {
        console.warn('Could not parse existing metadata, will replace');
      }
    }
    
    // Merge metadata (existing toast data takes precedence)
    const mergedMetadata = {
      ...removeTypename(newMetadata),
      ...existingMetadata,
      // Always update migration info
      lastMigration: {
        date: new Date().toISOString(),
        ...newMetadata
      }
    };
    
    // Update location with merged metadata
    await API.graphql(
      graphqlOperation(updateLocation, {
        input: {
          id: locationId,
          metadata: JSON.stringify(mergedMetadata)
        }
      })
    );
    
    return mergedMetadata;
    
  } catch (error) {
    console.error(`Failed to merge metadata for location ${locationId}:`, error);
    throw error;
  }
}